import { useState, useRef, useEffect } from "react";
import { ArrowLeft, Mail, CheckCircle, Lock, Eye, EyeOff } from "lucide-react";
import apiService from "../config.js";
import { useLanguage } from '../StoreContext/LanguageContext'; // Import language hook


// Main component for the forgot password flow
export default function ForgotPassword({ onBackToSignIn }) {
  // Steps: "request", "verify", "reset", "success"
  const [currentStep, setCurrentStep] = useState("request");
  const { language, translations } = useLanguage();

  const [resetData, setResetData] = useState({
    email: "",
    otp: ["", "", "", "", "", ""],
    newPassword: "",
    confirmPassword: ""
  });

  // Render the appropriate step
  const renderCurrentStep = () => {
    switch (currentStep) {
      case "request":
        return (
          <RequestReset 
            resetData={resetData}
            setResetData={setResetData}
            onSubmit={() => setCurrentStep("verify")}
            onBack={onBackToSignIn}
          />
        );
      case "verify":
        return (
          <VerifyOTP
            resetData={resetData}
            setResetData={setResetData}
            onSubmit={() => setCurrentStep("reset")}
            onBack={() => setCurrentStep("request")}
            onResend={() => {
              // Resend OTP by calling the email API again
              // handleResend();
            }}
          />
        );
      case "reset":
        return (
          <ResetPassword
            resetData={resetData}
            setResetData={setResetData}
            onSubmit={() => setCurrentStep("success")}
            onBack={() => setCurrentStep("verify")}
          />
        );
      case "success":
        return (
          <ResetSuccess
            onBackToSignIn={onBackToSignIn}
          />
        );
      default:
        return null;
    }
  };

  return (
    <div className="w-full">
      {renderCurrentStep()}
    </div>
  );
}

// API Functions using ApiService
const requestPasswordReset = async (email) => {
  try {
    const data = await apiService.requestPasswordReset(email);
    return { success: true, data };
  } catch (error) {
    console.error('Error requesting password reset:', error);
    return { success: false, error: error.message };
  }
};

const verifyOTP = async (email, otp) => {
  try {
    const data = await apiService.verifyPasswordOTP(email, otp);
    return { success: true, data };
  } catch (error) {
    console.error('Error verifying OTP:', error);
    return { success: false, error: error.message };
  }
};

const resetPassword = async (email, newPassword, confirmPassword) => {
  try {
    const data = await apiService.resetPassword(email, newPassword, confirmPassword);
    return { success: true, data };
  } catch (error) {
    console.error('Error resetting password:', error);
    return { success: false, error: error.message };
  }
};

// Step 1: Request Password Reset
function RequestReset({ resetData, setResetData, onSubmit, onBack }) {
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const { language, translations } = useLanguage();


  const handleInputChange = (e) => {
    const { value } = e.target;
    setResetData({
      ...resetData,
      email: value
    });
    
    if (error) {
      setError("");
    }
  };

  const validateForm = () => {
    if (!resetData.email) {
      setError("Email is required");
      return false;
    } else if (!/\S+@\S+\.\S+/.test(resetData.email)) {
      setError("Please enter a valid email address");
      return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (validateForm()) {
      setLoading(true);
      setError("");
      
      try {
        const result = await requestPasswordReset(resetData.email);
        
        if (result.success) {
          alert(`Password reset request sent to ${resetData.email}`);
          onSubmit();
        } else {
          setError(result.error || "Failed to send reset email. Please try again.");
        }
      } catch (error) {
        setError("Network error. Please check your connection and try again.");
      } finally {
        setLoading(false);
      }
    }
  };

  return (
    <>
      <div className="mb-6">
        <button
          onClick={onBack}
          className="inline-flex items-center text-sm font-medium text-gray-500 hover:text-gray-700"
          disabled={loading}
        >
          <ArrowLeft size={16} className="mr-1" />
          {translations[language].BackToSignIn}
        </button>
      </div>

      <div className="text-center mb-8">
        <h2 className="text-2xl font-bold text-gray-800">{translations[language].ForgotPassword}</h2>
        <p className="text-gray-600 mt-1">
          {translations[language].DontWorry}
        </p>
      </div>

      <div className="space-y-4">
        <div className="space-y-1">
          <label htmlFor="email" className="block text-sm font-medium text-gray-700">
           {translations[language].Email}
          </label>
          <div className="relative">
            <span className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
              <Mail size={18} className="text-gray-400" />
            </span>
            <input
              id="email"
              name="email"
              type="email"
              value={resetData.email}
              onChange={handleInputChange}
              disabled={loading}
              className={`w-full py-2 pl-10 pr-3 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                error ? "border-red-500" : "border-gray-300"
              } ${loading ? "opacity-50 cursor-not-allowed" : ""}`}
              placeholder="email@example.com"
            />
          </div>
          {error && (
            <p className="text-red-500 text-xs mt-1">{error}</p>
          )}
        </div>

        <div className="mt-2">
          <p className="text-sm text-gray-600">
          {translations[language].SendCodeInfo}
          </p>
        </div>

        <button
          onClick={handleSubmit}
          disabled={loading}
          className={`w-full py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white ${
            loading 
              ? "bg-gray-400 cursor-not-allowed" 
              : "bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          }`}
        >
          {loading ?<span>{translations[language].Sending}</span> : <span>{translations[language].SendVerificationCode}</span>}
        </button>
      </div>
    </>
  );
}

// Step 2: Verify OTP with individual boxes
function VerifyOTP({ resetData, setResetData, onSubmit, onBack, onResend }) {
  const [error, setError] = useState("");
  const { language, translations } = useLanguage();
  const [loading, setLoading] = useState(false);
  const [resending, setResending] = useState(false);
  const inputRefs = useRef([]);
  
  useEffect(() => {
    // Initialize refs array
    inputRefs.current = inputRefs.current.slice(0, 6);
    
    // Focus first input on component mount
    if (inputRefs.current[0]) {
      inputRefs.current[0].focus();
    }
  }, []);

  const handleInputChange = (index, value) => {
    // Allow only digits and update the OTP array
    if (/^[0-9]?$/.test(value)) {
      const newOtp = [...resetData.otp];
      newOtp[index] = value;
      
      setResetData({
        ...resetData,
        otp: newOtp
      });
      
      // If digit entered and not last box, move to next input
      if (value && index < 5) {
        inputRefs.current[index + 1].focus();
      }
      
      if (error) {
        setError("");
      }
    }
  };

  const handleKeyDown = (index, e) => {
    // Handle backspace
    if (e.key === "Backspace") {
      if (!resetData.otp[index] && index > 0) {
        // If current field is empty and backspace pressed, go to previous field
        inputRefs.current[index - 1].focus();
      }
    }
    // Handle left arrow
    else if (e.key === "ArrowLeft" && index > 0) {
      inputRefs.current[index - 1].focus();
    }
    // Handle right arrow
    else if (e.key === "ArrowRight" && index < 5) {
      inputRefs.current[index + 1].focus();
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData("text/plain").replace(/[^0-9]/g, "").slice(0, 6);
    
    if (pastedData) {
      const newOtp = [...resetData.otp];
      
      // Fill the OTP array with pasted digits
      for (let i = 0; i < pastedData.length; i++) {
        newOtp[i] = pastedData[i];
      }
      
      setResetData({
        ...resetData,
        otp: newOtp
      });
      
      // Focus last filled input or the next empty one
      const focusIndex = Math.min(pastedData.length, 5);
      inputRefs.current[focusIndex].focus();
    }
  };

  const validateOtp = () => {
    const otpValue = resetData.otp.join("");
    
    if (otpValue.length !== 6) {
      setError("Please enter all 6 digits of the verification code");
      return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (validateOtp()) {
      setLoading(true);
      setError("");
      
      try {
        const otpValue = resetData.otp.join("");
        const result = await verifyOTP(resetData.email, otpValue);
        
        if (result.success) {
          onSubmit();
        } else {
          setError(result.error || "Invalid verification code. Please try again.");
        }
      } catch (error) {
        setError("Network error. Please check your connection and try again.");
      } finally {
        setLoading(false);
      }
    }
  };

  const handleResend = async () => {
    setResending(true);
    setError("");
    
    try {
      const result = await requestPasswordReset(resetData.email);
      
      if (result.success) {
        alert("OTP resent successfully!");
        // Clear current OTP
        setResetData({
          ...resetData,
          otp: ["", "", "", "", "", ""]
        });
        // Focus first input
        if (inputRefs.current[0]) {
          inputRefs.current[0].focus();
        }
      } else {
        setError(result.error || "Failed to resend OTP. Please try again.");
      }
    } catch (error) {
      setError("Network error. Please try again.");
    } finally {
      setResending(false);
    }
  };

  return (
    <>
      <div className="mb-6">
        <button
          onClick={onBack}
          disabled={loading}
          className="inline-flex items-center text-sm font-medium text-gray-500 hover:text-gray-700"
        >
          <ArrowLeft size={16} className="mr-1" />
          {translations[language].Back}
        </button>
      </div>

      <div className="text-center mb-8">
        <h2 className="text-2xl font-bold text-gray-800">{translations[language].VerifyCode}</h2>
        <p className="text-gray-600 mt-1">
          {translations[language].EnterCodePrompt} {resetData.email}
        </p>
      </div>

      <div className="space-y-4">
        <div className="space-y-1">
          <label className="block text-sm font-medium text-gray-700 text-center mb-2">
            {translations[language].VerificationCode}
          </label>
          
          <div className="flex justify-center gap-2">
            {[0, 1, 2, 3, 4, 5].map((index) => (
              <input
                key={index}
                ref={(el) => (inputRefs.current[index] = el)}
                type="text"
                maxLength={1}
                value={resetData.otp[index]}
                onChange={(e) => handleInputChange(index, e.target.value)}
                onKeyDown={(e) => handleKeyDown(index, e)}
                onPaste={index === 0 ? handlePaste : null}
                disabled={loading}
                className={`w-12 h-12 text-center text-xl font-semibold border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                  loading ? "opacity-50 cursor-not-allowed" : ""
                }`}
              />
            ))}
          </div>
          
          {error && (
            <p className="text-red-500 text-xs mt-2 text-center">{error}</p>
          )}
        </div>

        <div className="flex justify-center">
          <button
            onClick={handleResend}
            disabled={resending || loading}
            className={`text-sm font-medium ${
              resending || loading
                ? "text-gray-400 cursor-not-allowed"
                : "text-blue-600 hover:text-blue-500"
            }`}
          >
            {resending ? <span>{translations[language].Resending}</span> :<span>{translations[language].DidntReceiveCode}</span>}
          </button>
        </div>

        <button
          onClick={handleSubmit}
          disabled={loading}
          className={`w-full py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white ${
            loading 
              ? "bg-gray-400 cursor-not-allowed" 
              : "bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          }`}
        >
          {loading ? <span>{translations[language].Verifying}</span> :<span>{translations[language].VerifyAndContinue}</span>}
        </button>
      </div>
    </>
  );
}

// Step 3: Reset Password
function ResetPassword({ resetData, setResetData, onSubmit, onBack }) {
  const [showPassword, setShowPassword] = useState(false);
  const { language, translations } = useLanguage();
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({
    newPassword: "",
    confirmPassword: ""
  });

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setResetData({
      ...resetData,
      [name]: value
    });
    
    // Clear error when user types
    if (errors[name]) {
      setErrors({
        ...errors,
        [name]: ""
      });
    }
    
    // Special handling for confirmPassword - validate on change
    if (name === "confirmPassword" || (name === "newPassword" && resetData.confirmPassword)) {
      const passwordValue = name === "newPassword" ? value : resetData.newPassword;
      const confirmValue = name === "confirmPassword" ? value : resetData.confirmPassword;
      
      if (passwordValue && confirmValue && passwordValue !== confirmValue) {
        setErrors(prev => ({
          ...prev,
          confirmPassword: "Passwords don't match"
        }));
      } else if (errors.confirmPassword) {
        setErrors(prev => ({
          ...prev,
          confirmPassword: ""
        }));
      }
    }
  };

  const validateForm = () => {
    let isValid = true;
    const newErrors = { newPassword: "", confirmPassword: "" };
    
    // Password validation with strength requirements
    if (!resetData.newPassword) {
      newErrors.newPassword = "Password is required";
      isValid = false;
    } else if (resetData.newPassword.length < 6) {
      newErrors.newPassword = "Password must be at least 6 characters";
      isValid = false;
    } else if (!/(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/.test(resetData.newPassword)) {
      newErrors.newPassword = "Password must contain at least one uppercase letter, one lowercase letter, and one number";
      isValid = false;
    }
    
    // Confirm password validation
    if (!resetData.confirmPassword) {
      newErrors.confirmPassword = "Please confirm your password";
      isValid = false;
    } else if (resetData.newPassword !== resetData.confirmPassword) {
      newErrors.confirmPassword = "Passwords don't match";
      isValid = false;
    }
    
    setErrors(newErrors);
    return isValid;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (validateForm()) {
      setLoading(true);
      
      try {
        const result = await resetPassword(
          resetData.email, 
          resetData.newPassword, 
          resetData.confirmPassword
        );
        
        if (result.success) {
          onSubmit();
        } else {
          setErrors({
            newPassword: result.error || "Failed to reset password. Please try again.",
            confirmPassword: ""
          });
        }
      } catch (error) {
        setErrors({
          newPassword: "Network error. Please check your connection and try again.",
          confirmPassword: ""
        });
      } finally {
        setLoading(false);
      }
    }
  };

  return (
    <>
      <div className="mb-6">
        <button
          onClick={onBack}
          disabled={loading}
          className="inline-flex items-center text-sm font-medium text-gray-500 hover:text-gray-700"
        >
          <ArrowLeft size={16} className="mr-1" />
          {translations[language].Back}
        </button>
      </div>

      <div className="text-center mb-8">
        <h2 className="text-2xl font-bold text-gray-800">{translations[language].ResetPassword}</h2>
        <p className="text-gray-600 mt-1">
          {translations[language].NewPasswordPrompt}
        </p>
      </div>

      <div className="space-y-4">
        <div className="space-y-1">
          <label htmlFor="newPassword" className="block text-sm font-medium text-gray-700">
           {translations[language].NewPassword}
          </label>
          <div className="relative">
            <span className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
              <Lock size={18} className="text-gray-400" />
            </span>
            <input
              id="newPassword"
              name="newPassword"
              type={showPassword ? "text" : "password"}
              value={resetData.newPassword}
              onChange={handleInputChange}
              disabled={loading}
              className={`w-full py-2 pl-10 pr-10 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                errors.newPassword ? "border-red-500" : "border-gray-300"
              } ${loading ? "opacity-50 cursor-not-allowed" : ""}`}
              placeholder="••••••••"
            />
            <button
              type="button"
              className="absolute inset-y-0 right-0 flex items-center pr-3"
              onClick={() => setShowPassword(!showPassword)}
              disabled={loading}
            >
              {showPassword ? (
                <EyeOff size={18} className="text-gray-400" />
              ) : (
                <Eye size={18} className="text-gray-400" />
              )}
            </button>
          </div>
          {errors.newPassword && (
            <p className="text-red-500 text-xs mt-1">{errors.newPassword}</p>
          )}
        </div>

        <div className="space-y-1">
          <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700">
            {translations[language].ConfirmPassword}
          </label>
          <div className="relative">
            <span className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
              <Lock size={18} className="text-gray-400" />
            </span>
            <input
              id="confirmPassword"
              name="confirmPassword"
              type={showConfirmPassword ? "text" : "password"}
              value={resetData.confirmPassword}
              onChange={handleInputChange}
              disabled={loading}
              className={`w-full py-2 pl-10 pr-10 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                errors.confirmPassword ? "border-red-500" : "border-gray-300"
              } ${loading ? "opacity-50 cursor-not-allowed" : ""}`}
              placeholder="••••••••"
            />
            <button
              type="button"
              className="absolute inset-y-0 right-0 flex items-center pr-3"
              onClick={() => setShowConfirmPassword(!showConfirmPassword)}
              disabled={loading}
            >
              {showConfirmPassword ? (
                <EyeOff size={18} className="text-gray-400" />
              ) : (
                <Eye size={18} className="text-gray-400" />
              )}
            </button>
          </div>
          {errors.confirmPassword && (
            <p className="text-red-500 text-xs mt-1">{errors.confirmPassword}</p>
          )}
        </div>

        <button
          onClick={handleSubmit}
          disabled={loading}
          className={`w-full py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white ${
            loading 
              ? "bg-gray-400 cursor-not-allowed" 
              : "bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          }`}
        >
          {loading ? <span>{translations[language].Resetting}</span> : <span>{translations[language].ResetPassword}</span>}
        </button>
      </div>
    </>
  );
}

// Step 4: Success Message
function ResetSuccess({ onBackToSignIn }) {
    const { language, translations } = useLanguage();

  return (
    <div className="text-center py-8">
      <div className="flex justify-center mb-4">
        <CheckCircle size={64} className="text-green-500" />
      </div>
      <h2 className="text-2xl font-bold text-gray-800 mb-2">Password Reset Successful!</h2>
      <p className="text-gray-600 mb-6">
        Your password has been reset successfully. You can now sign in with your new password.
      </p>
      <button
        onClick={onBackToSignIn}
        className="py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
      >
        {translations[language].BackToSignIn}
      </button>
    </div>
  );
}